import React, { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import "./ProjectsCollab.css";

/*
 * UniTech Engineering — Comment ça marche ? / Premium V1
 * Nom du composant et point d'import conservés.
 * Schéma de principe volontairement simplifié, sans architecture cloud.
 */
const CONTENT = {
  fr: {
    label: "Comment ça marche ?",
    title: "Connecter. Comprendre. Agir.",
    intro: "Le fonctionnement de votre parc devient plus lisible, étape par étape.",
    steps: {
      connect: {
        title: "Connectez",
        description: "Une passerelle adaptée recueille les informations disponibles sur l'ascenseur."
      },
      supervise: {
        title: "Supervisez",
        description: "OPENlift organise les données et vous donne de la visibilité sur le fonctionnement."
      },
      act: {
        title: "Intervenez mieux",
        description: "Vos équipes disposent d'informations utiles pour leurs décisions et interventions."
      }
    },
    diagram: {
      title: "Une chaîne simple et lisible.",
      disclaimer: "Schéma de principe — non contractuel",
      elevator: { title: "Ascenseur", detail: "Informations disponibles", tag: "Source" },
      gateway: { title: "Passerelle connectée", detail: "Collecte et transmission", tag: "Connecter" },
      platform: { title: "OPENlift + équipes", detail: "Suivi, compréhension et intervention", tag: "Agir" }
    }
  },
  en: {
    label: "How does it work?",
    title: "Connect. Understand. Act.",
    intro: "Make your fleet's operation easier to understand, step by step.",
    steps: {
      connect: {
        title: "Connect",
        description: "A suitable gateway collects the information available from the elevator."
      },
      supervise: {
        title: "Monitor",
        description: "OPENlift organizes the data and provides visibility into equipment operation."
      },
      act: {
        title: "Take better action",
        description: "Your teams have useful information to support decisions and interventions."
      }
    },
    diagram: {
      title: "A clear, simple flow.",
      disclaimer: "Conceptual diagram — non-contractual",
      elevator: { title: "Elevator", detail: "Available information", tag: "Source" },
      gateway: { title: "Connected gateway", detail: "Collection and transmission", tag: "Connect" },
      platform: { title: "OPENlift + teams", detail: "Monitoring, insight and intervention", tag: "Act" }
    }
  }
};

const STEP_IDS = [
  { key: "connect", number: "01" },
  { key: "supervise", number: "02" },
  { key: "act", number: "03" }
];
const FLOW_IDS = ["elevator", "gateway", "platform"];

export default function ProjectsCollab() {
  const { t, i18n } = useTranslation();
  const sectionRef = useRef(null);
  const language = (i18n.resolvedLanguage || i18n.language || "fr")
    .split("-")[0].toLowerCase();
  const fallback = CONTENT[language] || CONTENT.fr;

  // Un seul reveal à l'entrée dans la zone. Le contenu reste visible sans JS.
  useEffect(() => {
    const section = sectionRef.current;
    if (!section || typeof IntersectionObserver === "undefined") return undefined;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        section.classList.add("uc-how--seen");
        observer.disconnect();
      }
    }, { threshold: 0.15 });
    observer.observe(section);
    return () => observer.disconnect();
  }, []);

  const label = t("projectsCollab.howItWorks.label", { defaultValue: fallback.label });
  const title = t("projectsCollab.howItWorks.title", { defaultValue: fallback.title });
  const intro = t("projectsCollab.howItWorks.intro", { defaultValue: fallback.intro });
  const steps = STEP_IDS.map(({ key, number }) => ({
    key,
    number,
    title: t(`projectsCollab.howItWorks.steps.${key}.title`, {
      defaultValue: fallback.steps[key].title
    }),
    description: t(`projectsCollab.howItWorks.steps.${key}.description`, {
      defaultValue: fallback.steps[key].description
    })
  }));
  const diagramTitle = t("projectsCollab.howItWorks.diagram.title", {
    defaultValue: fallback.diagram.title
  });
  const disclaimer = t("projectsCollab.howItWorks.diagram.disclaimer", {
    defaultValue: fallback.diagram.disclaimer
  });
  const flow = FLOW_IDS.map((key) => ({
    key,
    title: t(`projectsCollab.howItWorks.diagram.${key}.title`, {
      defaultValue: fallback.diagram[key].title
    }),
    detail: t(`projectsCollab.howItWorks.diagram.${key}.detail`, {
      defaultValue: fallback.diagram[key].detail
    }),
    tag: t(`projectsCollab.howItWorks.diagram.${key}.tag`, {
      defaultValue: fallback.diagram[key].tag
    })
  }));

  return (
    <section className="uc-how" aria-labelledby="uc-how-title" ref={sectionRef}>
      <div className="uc-how__container">
        <div className="uc-how__layout">
          <div className="uc-how__editorial">
            <header className="uc-how__header">
              <p className="uc-how__eyebrow">{label}</p>
              <h2 id="uc-how-title" className="uc-how__title">{title}</h2>
              <p className="uc-how__intro">{intro}</p>
            </header>

            <ol className="uc-how__steps">
              {steps.map((step) => (
                <li className="uc-how__step" key={step.key}>
                  <span className="uc-how__number" aria-hidden="true">{step.number}</span>
                  <div className="uc-how__step-copy">
                    <h3 className="uc-how__step-title">{step.title}</h3>
                    <p className="uc-how__step-text">{step.description}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>

          <figure className="uc-how__diagram">
            <figcaption className="uc-how__diagram-header">
              <strong className="uc-how__diagram-title">{diagramTitle}</strong>
              <span className="uc-how__diagram-disclaimer">{disclaimer}</span>
            </figcaption>
            <ol className="uc-how__flow" aria-label={diagramTitle}>
              {flow.map((item, index) => (
                <li className="uc-how__flow-item" key={item.key}>
                  <div className="uc-how__flow-copy">
                    <span className="uc-how__flow-title">{item.title}</span>
                    <span className="uc-how__flow-detail">{item.detail}</span>
                  </div>
                  <span className="uc-how__flow-tag" aria-hidden="true">{item.tag}</span>
                  {index < flow.length - 1 && (
                    <svg className="uc-how__connector" viewBox="0 0 24 42" fill="none" aria-hidden="true" focusable="false">
                      <path className="uc-how__connector-line" d="M12 1V32" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeDasharray="3 5" />
                      <path className="uc-how__connector-head" d="m6.5 27 5.5 6 5.5-6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  )}
                </li>
              ))}
            </ol>
          </figure>
        </div>
      </div>
    </section>
  );
}
